import { AirportAutocomplete } from "@airhelp/react";
import type { Airport as AirportProps, AirportOption } from "@airhelp/react";
import { useCallback, useState, useMemo } from "react";
import type { Dispatch, SetStateAction } from "react";
import debounce from "lodash/debounce";
import { getAirports } from "services/webapp";
import { trackSearchEvent } from "utils/tracking";
import type { AutocompleteInputProps } from "./AutocompleteInput.d";

const updateAirports = debounce((query: string, setAirports: Dispatch<SetStateAction<AirportProps[]>>) => {
  getAirports(query).then((airports) => {
    setAirports(airports);
  });
}, 300);

export const AutocompleteInput = ({
  autoFocus,
  placeholder,
  setAirportSelected,
  instanceId,
  dataTestId,
  icon,
  disabledAirports,
  value,
}: AutocompleteInputProps) => {
  const [inputValue, setInputValue] = useState("");
  const [currentAirport, setCurrentAirport] = useState<AirportProps>(null);
  const [airports, setAirports] = useState<AirportProps[]>([]);

  const updateDropdownList = useCallback((query: string) => updateAirports(query, setAirports), []);

  const airportOptions = useMemo(
    () =>
      airports?.map((airport) => ({
        value: airport,
        isDisabled: disabledAirports.some(
          (disabledAirport) => airport.name === disabledAirport?.name && currentAirport?.name !== airport.name
        ),
      })),
    [disabledAirports, airports, currentAirport]
  );

  const handleInputChange = (value: string) => {
    setInputValue(value);
    if (!value) return setAirports([]);

    return updateDropdownList(value);
  };

  const handleAirportChange = (option: AirportOption) => {
    const airportName = option?.value?.name;

    if (airportName) {
      const searchType = instanceId === "departure-input" ? "departure" : "destination";

      trackSearchEvent({ event: "search_airport", type: searchType, searchPhrase: inputValue, label: airportName });
    }
    setAirportSelected(option?.value);
    setCurrentAirport(option?.value);
  };

  return (
    <AirportAutocomplete
      menuIsOpen={airportOptions?.length > 0}
      options={airportOptions}
      onInputChange={handleInputChange}
      autoFocus={autoFocus}
      placeholder={placeholder}
      handleChange={handleAirportChange}
      instanceId={instanceId}
      dataTestId={dataTestId}
      aria-label={placeholder.toString()}
      icon={icon}
      tabSelectsValue
      value={value}
    />
  );
};
